import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { SharedDocumentService } from '@luggagehero/shared/services/document';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { ModalDirective } from 'ngx-bootstrap/modal';

import { BaseComponent } from '../../../core';
import { RouterExtensions } from '../../../core/services/index';

// HACK: We shouldn't use this in the component code
const NAVBAR_HEIGHT = 60;

@Component({ template: '' })
export abstract class AcceptTermsOfServiceBaseComponent extends BaseComponent implements OnInit {
  @ViewChild('tosAcceptanceModal') public tosAcceptanceModal: ModalDirective;
  @ViewChild('phone') public phoneInput: FormControl;
  public documentPosition: number;
  public documentHeight: number;
  public isProgressBarRequired: boolean;
  public isTermsOfServiceReviewed = false;
  public isPhoneNumberFormSubmitted = false;
  public isConfirmationCodeSent = false;
  public phoneNumberForm: FormGroup;
  public phoneCountries = AppConfig.SUPPORTED_PHONE_COUNTRIES;

  private scrollEventListener: (this: Window, ev: Event) => unknown;
  private _phoneNumber: string;
  private _phoneCountry: string;
  private _isLoading = false;

  constructor(
    formBuilder: FormBuilder,
    private userService: SharedUserService,
    private documentService: SharedDocumentService,
    private router: RouterExtensions,
    private cd: ChangeDetectorRef,
  ) {
    super();
    this.phoneNumberForm = formBuilder.group({
      // eslint-disable-next-line @typescript-eslint/unbound-method
      phone: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get storageManagerPhoneNumber(): string {
    if (!this.userService.isLoggedIn || !this.userService.user) {
      return '';
    }
    return this.userService.user.phone;
  }

  get storageManagerPhoneNumberLast4(): string {
    if (!this.storageManagerPhoneNumber) {
      return '';
    }
    const last4 = this.storageManagerPhoneNumber.slice(this.storageManagerPhoneNumber.length - 4);

    let padding = '';
    let paddingCount = this.storageManagerPhoneNumber.length - 4;

    // TODO: Don't include padding for country code
    while (paddingCount-- > 0) {
      padding += '* ';
    }
    return `${padding}${last4}`;
  }

  get phoneNumber(): string {
    return this._phoneNumber;
  }
  set phoneNumber(value: string) {
    this._phoneNumber = value;
    this.cd.markForCheck();
  }

  get phoneCountry(): string {
    return this._phoneCountry;
  }
  set phoneCountry(value: string) {
    this._phoneCountry = value;
    this.cd.markForCheck();
  }

  get phoneInputError(): string {
    if (!this.isPhoneNumberFormSubmitted) {
      return undefined;
    }
    const phoneInput = this.phoneNumberForm.get('phone');
    if (!phoneInput.errors) {
      return undefined;
    }
    if (phoneInput.errors.required) {
      return 'PHONE_NUMBER_MISSING';
    }
    if (phoneInput.errors.minlength) {
      return 'PHONE_NUMBER_TOO_SHORT';
    }
    if (phoneInput.errors.invalid) {
      return (phoneInput.errors.invalid as { reason?: string })?.reason;
    }
    return 'INVALID_PHONE_NUMBER';
  }

  get country(): string {
    return 'DK';
  }

  ngOnInit() {
    this.scrollEventListener = this.updateReviewProgress.bind(this) as typeof this.updateReviewProgress;
    this.documentService.document.addEventListener('scroll', this.scrollEventListener);

    this.updateReviewProgress();
  }

  async startTosAcceptance() {
    if (this.storageManagerPhoneNumber) {
      this.isLoading = true;

      try {
        await this.requestTosAcceptance();
      } catch (err) {
        // TODO: Notify user
      }
      this.isLoading = false;
    }
    this.tosAcceptanceModal.show();
  }

  async savePhoneNumber() {
    this.isPhoneNumberFormSubmitted = true;

    if (this.phoneInputError) {
      return;
    }
    this.isLoading = true;

    try {
      // Set phone number on user object
      const currentUser = this.userService.user;
      currentUser.phone = this.phoneNumber;
      currentUser.phoneCountry = this.phoneCountry;

      // Update the user via the API
      await this.userService.updateUser(currentUser);

      // Request one-time code to be sent to the user by SMS
      await this.requestTosAcceptance();
    } catch (err) {
      // TODO: Notify user of error
    }

    this.isLoading = false;
  }

  async resendConfirmationCode() {
    this.isConfirmationCodeSent = false;
    this.isLoading = true;

    await this.requestTosAcceptance();

    this.isConfirmationCodeSent = true;
    this.isLoading = false;
  }

  async requestTosAcceptance() {
    // TODO: Request one-time code via API
    console.log(`Requesting TOS acceptance...`);
    await new Promise((resolve) => setTimeout(resolve, 1600));
  }

  async confirmTosAcceptance(code: string) {
    try {
      // TODO: Confirm acceptance via API using the given code
      console.log(`Confirming TOS acceptance: ${code}...`);
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // TODO: Navigate on success
      void this.router.navigate(['/dashboard']);
    } catch (err) {
      // TODO: Display error on failure
    }

    // TODO: Display error on failure
  }

  async onCodeCompleted(code: string) {
    this.isLoading = true;
    await this.confirmTosAcceptance(code);
    this.isLoading = false;
  }

  private updateReviewProgress() {
    this.isProgressBarRequired =
      this.documentService.scrollHeight - this.documentService.scrollClientHeight - NAVBAR_HEIGHT > 0;

    this.documentPosition = Math.floor(this.documentService.scrollPosition);
    this.documentHeight = this.documentService.scrollHeight - this.documentService.scrollClientHeight;

    const remainingScroll = this.documentHeight - this.documentPosition;
    const tosReviewed = remainingScroll <= NAVBAR_HEIGHT;

    if (tosReviewed) {
      // Stop listening for scroll events
      this.documentService.document.removeEventListener('scroll', this.scrollEventListener);

      // Make sure progress bar is at 100%
      this.documentPosition = this.documentHeight;

      // Enable accept button
      this.isTermsOfServiceReviewed = true;
    }

    this.cd.markForCheck();
  }
}
